/**
 * Compress and normalize an image File to fit within agent provider limits:
 * - Max 5 MB image payload (we target 4.5 MB for safety)
 * - Max 8000px per dimension (we target 7500px for safety)
 * - AVIF is converted to JPEG because the agent model cannot consume AVIF
 *
 * Uses the browser Canvas API to re-encode the image as JPEG at progressively
 * lower quality / smaller dimensions until it fits.
 *
 * Non-image files pass through unchanged.
 */

export const IMAGE_AUTO_SCALE_THRESHOLD_BYTES = 10 * 1024 * 1024;
const PROVIDER_MAX_IMAGE_BYTES = 5 * 1024 * 1024;
const SAFE_TARGET_BYTES = 4.5 * 1024 * 1024;
const MAX_DIMENSION = 7500; // Anthropic limit is 8000, use 7500 for safety
const NORMALIZED_IMAGE_TYPES = new Set(['image/avif']);
const OUTPUT_IMAGE_TYPE = 'image/jpeg';
const AVIF_CONVERSION_ERROR =
  'AVIF images could not be converted. Please upload JPG, PNG, WebP, GIF, SVG, or PDF instead.';

/**
 * Returns the File as-is if it's not an image, or if it is already in a
 * supported format and within limits. Otherwise returns a JPEG File.
 */
export async function compressImageForUpload(file: File): Promise<File> {
  const fileType =
    file.type ||
    (file.name.toLowerCase().endsWith('.avif') ? 'image/avif' : '');

  if (!fileType.startsWith('image/')) {
    return file;
  }

  const needsFormatNormalization = NORMALIZED_IMAGE_TYPES.has(fileType);
  if (!needsFormatNormalization && file.size <= PROVIDER_MAX_IMAGE_BYTES) {
    return file;
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch (error) {
    console.warn('[compressImage] Could not decode image locally', error);
    if (needsFormatNormalization) {
      throw new Error(AVIF_CONVERSION_ERROR);
    }
    return file;
  }
  const { width, height } = bitmap;

  const needsSizeReduction = file.size > PROVIDER_MAX_IMAGE_BYTES;
  const needsDimensionReduction =
    width > MAX_DIMENSION || height > MAX_DIMENSION;

  // Already within all limits and in a model-supported format — no work needed
  if (
    !needsFormatNormalization &&
    !needsSizeReduction &&
    !needsDimensionReduction
  ) {
    bitmap.close();
    return file;
  }

  console.log(
    `[compressImage] "${file.name}" ${width}x${height} ${(file.size / 1024 / 1024).toFixed(1)} MB — converting to JPEG` +
      (needsFormatNormalization ? ' (format unsupported by agent)' : '') +
      (needsDimensionReduction ? ' (dimensions exceed limit)' : '') +
      (needsSizeReduction ? ' (size exceeds limit)' : ''),
  );

  // Calculate initial scale to bring dimensions within the max
  let dimensionScale = 1;
  if (needsDimensionReduction) {
    dimensionScale = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
  }

  let quality = 0.85;
  const MIN_QUALITY = 0.4;
  let scale = dimensionScale;
  let blob: Blob | null = null;

  // Always run at least one pass (to enforce dimension limit even if size is fine)
  do {
    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);

    const canvas = new OffscreenCanvas(targetWidth, targetHeight);
    const ctx = canvas.getContext('2d');
    if (!ctx) break;

    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    blob = await canvas.convertToBlob({ type: OUTPUT_IMAGE_TYPE, quality });

    console.log(
      `[compressImage] quality=${quality.toFixed(2)} scale=${Math.round(scale * 100)}% → ${(blob.size / 1024 / 1024).toFixed(2)} MB (${targetWidth}x${targetHeight})`,
    );

    if (blob.size <= SAFE_TARGET_BYTES) break;

    // Reduce quality first, then start scaling down
    if (quality > MIN_QUALITY) {
      quality -= 0.15;
    } else if (scale > 0.3) {
      scale -= 0.15;
      quality = 0.7; // Reset quality for the smaller size
    } else {
      break; // Can't reduce further
    }
  } while (quality >= MIN_QUALITY);

  bitmap.close();

  if (!blob || blob.size > PROVIDER_MAX_IMAGE_BYTES) {
    console.warn(
      '[compressImage] Could not compress below limit, returning original',
    );
    if (needsFormatNormalization) {
      throw new Error(AVIF_CONVERSION_ERROR);
    }
    return file;
  }

  // Build a new File with the original name (but .jpg extension)
  const nameWithoutExt = file.name.replace(/\.[^.]+$/, '');
  const compressedFile = new File([blob], `${nameWithoutExt}.jpg`, {
    type: OUTPUT_IMAGE_TYPE,
    lastModified: Date.now(),
  });

  console.log(
    `[compressImage] Compressed ${(file.size / 1024 / 1024).toFixed(1)} MB → ${(compressedFile.size / 1024 / 1024).toFixed(1)} MB`,
  );

  return compressedFile;
}
